import { selectOptionsFromArrayOfFives } from 'components/views/forms/rates/RateDiscounts/helpers';
import { SelectOptions } from 'components/core/Select/AppSelect';
import { dateFormatLongMonth, convertDateStringToDate, OfferAvailabilityId, OfferTypeId } from '@alluradirect/react';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import type { Guest, OwnerOfferInput } from './OwnerOfferQueries';

const AVAILABLE_PERCENTAGES = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const AVAILABLE_NIGHTS_REQUIRED = [2, 3, 4, 5, 6, 7, 8, 9, 10];
const AVAILABLE_NIGHTS_FREE = [1, 2, 3, 4, 5];

export const selectOptionsFromArrayOfNumbers = (
  arr: number[],
  toLabel: (amount: number) => string,
): SelectOptions<number> => {
  return arr.map(amount => ({
    value: amount,
    label: toLabel(amount),
  }));
};

export const getPercentageOptions = (min?: number | '') => {
  const percentages = min ? AVAILABLE_PERCENTAGES.filter(amount => amount >= min) : AVAILABLE_PERCENTAGES;
  return selectOptionsFromArrayOfFives(percentages);
};
export const getNightsRequiredOptions = (min?: number | '') => {
  const nights = min ? AVAILABLE_NIGHTS_REQUIRED.filter(amount => amount >= min) : AVAILABLE_NIGHTS_REQUIRED;
  return selectOptionsFromArrayOfNumbers(nights, amount => `${amount}+ Nights`);
};
export const getNightsFreeOptions = (min?: number | '') => {
  const nights = min ? AVAILABLE_NIGHTS_FREE.filter(amount => amount >= min) : AVAILABLE_NIGHTS_FREE;
  return selectOptionsFromArrayOfNumbers(nights, amount => amount.toString());
};

const getDateStringOrUndefined = (date: string | undefined) => {
  return date ? dateFormatLongMonth(date) : undefined;
};

export const getDateOrUndefined = (date: string | undefined) => {
  return date ? convertDateStringToDate(date) : undefined;
};

export const getValidDisplay = (start: string | undefined, end: string | undefined, allowSingleDay?: boolean) => {
  const startDate = getDateStringOrUndefined(start);
  const endDate = getDateStringOrUndefined(end);

  if (!startDate && !endDate) return 'Always - Does not expire';

  if (!startDate) return `Until ${endDate}`;

  if (!endDate) return `Starting ${startDate}`;

  if (allowSingleDay && startDate === endDate) return startDate;

  return `${startDate} - ${endDate}`;
};

export const getDatesDisplay = (start: string, end: string, allowSingleDay?: boolean) => {
  const startDate = getDateStringOrUndefined(start);
  const endDate = getDateStringOrUndefined(end);

  if (!startDate && !endDate) return 'Valid for any bookable dates';

  if (!startDate) return `Until ${endDate}`;

  if (!endDate) return `Starting ${startDate}`;

  if (allowSingleDay && startDate === endDate) return startDate;

  return `${startDate} - ${endDate}`;
};

export const getNameForReserveeFromEmail = (GUEST_EMAIL: string, guestList: Guest[]) => {
  const guest = guestList.find(({ EMAIL }) => EMAIL === GUEST_EMAIL);
  if (!guest) return GUEST_EMAIL;
  return `${guest.FIRST_NAME} ${guest.LAST_NAME}`;
};

export const getOfferAvailabilityDisplay = (
  offer: {
    OFFER_AVAILABILITY_ID?: OfferAvailabilityId;
    GUEST_EMAIL?: string;
  },
  guestList?: {
    FIRST_NAME: string;
    LAST_NAME: string;
    EMAIL: string;
  }[],
) => {
  const { OFFER_AVAILABILITY_ID, GUEST_EMAIL } = offer;

  switch (OFFER_AVAILABILITY_ID) {
    case OfferAvailabilityId.AllGuests:
      return 'All Guests';
    case OfferAvailabilityId.RepeatGuests:
      return 'Repeat Guests';
    case OfferAvailabilityId.SpecificGuest: {
      if (!GUEST_EMAIL) return '-';
      if (!guestList) return GUEST_EMAIL;
      const guest = guestList.find(guest => guest.EMAIL === GUEST_EMAIL);
      return guest ? `${guest.FIRST_NAME} ${guest.LAST_NAME}` : GUEST_EMAIL;
    }
    default:
      return undefined;
  }
};

export const getOfferTypeDisplay = (offer: {
  OFFER_TYPE_ID?: OfferTypeId;
  DOLLAR?: string;
  NIGHTS_REQUIRED?: number | string;
  NIGHTS_FREE?: number | string;
  PERCENTAGE?: number | string;
}) => {
  const { OFFER_TYPE_ID, DOLLAR, NIGHTS_FREE, NIGHTS_REQUIRED, PERCENTAGE, } = offer;

  switch (OFFER_TYPE_ID) {
    case OfferTypeId.PERCENTAGE:
      if (!PERCENTAGE) return 'Percentage';
      return `Percentage (${PERCENTAGE}%)`;
    case OfferTypeId.DOLLAR:
      if (!DOLLAR) return 'Dollar';
      return `Dollar (${formatCanadianCurrency(DOLLAR)})`;
    case OfferTypeId.FREE_NIGHTS:
      if (!NIGHTS_REQUIRED || !NIGHTS_FREE) return 'Free Nights';
      return `Book ${NIGHTS_REQUIRED} get ${NIGHTS_FREE} free`;
    case OfferTypeId.GIFT_CERTIFICATE:
      if (!DOLLAR) return 'Gift/ Gift Certificate';
      return `Gift (${formatCanadianCurrency(DOLLAR)})`;
    default:
      return undefined;
  }
};


export const getOfferNightsDisplay = (offer: { MIN_NIGHTS: string }) => {
  const { MIN_NIGHTS } = offer;
  const MIN_NIGHTS_DISPLAY = MIN_NIGHTS ? `${MIN_NIGHTS}` : '';
  return MIN_NIGHTS_DISPLAY;
};

export const getOfferPropertyDisplay = (
  offer: {
    PROPERTY_IDS: string;
    IS_ALL_PROPERTIES: boolean;
  },
  propertyList?: { ADDRESS: string; ID: number }[],
) => {
  const { PROPERTY_IDS, IS_ALL_PROPERTIES } = offer;

  if (IS_ALL_PROPERTIES) return 'All Properties';

  if (!PROPERTY_IDS) return '-';
  if (!propertyList) return PROPERTY_IDS;

  const propertyIds = PROPERTY_IDS.split(',').map(id => parseInt(id, 10));
  const properties = propertyIds.map(id => {
    const property = propertyList.find(property => property.ID === id);
    return `${property?.ADDRESS || ''} (${id})`;
  });
  return properties.join(', ');
};

export const getOfferPublicDisplay = (offer: { IS_PUBLIC: boolean }) => {
  const { IS_PUBLIC } = offer;

  return IS_PUBLIC ? 'Public' : 'Private';
};

export const getBlackoutPeriodsDisplay = (offer: { BLACKOUT_PERIODS: { START: string; END: string }[] }) => {
  const { BLACKOUT_PERIODS } = offer;

  if (!BLACKOUT_PERIODS?.length) return '-';

  return BLACKOUT_PERIODS.map(({ START, END }) => getDatesDisplay(START, END, true)).join(', ');
};

const getTitleOfferAmountDisplay = (offer: {
  OFFER_TYPE_ID?: OfferTypeId;
  DESCRIPTION?: string;
  DOLLAR?: string;
  INSTRUCTIONS?: string;
  NIGHTS_REQUIRED?: number | string;
  NIGHTS_FREE?: number | string;
  PERCENTAGE?: number | string;
  MAX_USAGE_COUNT?: number;
}) => {
  const { OFFER_TYPE_ID, DOLLAR, NIGHTS_FREE, NIGHTS_REQUIRED, PERCENTAGE, } = offer;

  if (!OFFER_TYPE_ID) return '';

  switch (OFFER_TYPE_ID) {
    case OfferTypeId.PERCENTAGE:
      if (!PERCENTAGE) return '';
      return `${PERCENTAGE}% off`;
    case OfferTypeId.DOLLAR:
      if (!DOLLAR) return '';
      return `Save ${formatCanadianCurrency(DOLLAR)}`;
    case OfferTypeId.FREE_NIGHTS:
      if (!NIGHTS_REQUIRED || !NIGHTS_FREE) return '';
      return `Book ${NIGHTS_REQUIRED}+ nights and get ${NIGHTS_FREE} free`;
    case OfferTypeId.GIFT_CERTIFICATE:
      if (!DOLLAR) return 'Gift/ Gift Certificate';
      return `Gift (${formatCanadianCurrency(DOLLAR)})`;
    default:
      return '';
  }
};

export const getDefaultOfferTitle = (offer: OwnerOfferInput) => {
  const amountDisplay = getTitleOfferAmountDisplay(offer);
  if (!amountDisplay) return '';
  return `${amountDisplay}`;
};

export const isArrayStrEqual = (arr1: string | any[], arr2: string | any[]) => {
  const array1 = Array.isArray(arr1) ? arr1.map(v => v.toString()) : arr1.split(',');
  const array2 = Array.isArray(arr2) ? arr2.map(v => v.toString()) : arr2.split(',');
  array1.sort();
  array2.sort();
  return array1.join(',') === array2.join(',');
}

export const parseCommaSeparatedNum = (str: string) => {
  const array = str.split(',');
  return array.map(v => v.trim()).filter(Boolean).map(Number)
}

